import { useState } from "react";
import { Copy, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { copyText } from "@/lib/clipboard";

// Public ad preview. Reproduces the Bad Ass Horse Sales advertisement TEMPLATE
// as plain advertisement text. Admin view shows two versions of the SAME master ad:
// Facebook Group (original) and FB Page & Website (+ FB Group bidding link).
const TABS = [
  ["facebook_group", "Facebook Group"],
  ["page_website", "FB Page & Website"],
];

export default function AdPreview({ preview, showVersions = true, stickyBar = false }) {
  const [tab, setTab] = useState("facebook_group");
  if (!preview) return null;
  const versions = showVersions ? preview.versions : null;
  const current = versions ? versions[tab] : { sections: preview.sections, ad_text: preview.ad_text, available: true };
  const sections = current.sections || [];
  const warnings = preview.warnings || [];
  const canCopy = current.available && !!current.ad_text;

  const copy = () =>
    copyText(current.ad_text || "", tab === "page_website" && versions
      ? "FB Page & Website ad copied"
      : "Ad text copied — ready to paste into Facebook");

  return (
    <div className="mx-auto w-full max-w-[600px]">
      <div data-testid="ad-preview-bar" className={stickyBar ? "sticky -top-4 z-10 -mx-1 px-1 pt-4 pb-1 bg-[#FAF8F5]" : ""}>
      {versions && (
        <div data-testid="ad-version-tabs" role="tablist" className="grid grid-cols-2 gap-1 p-1 mb-3 bg-stone-100 rounded-lg">
          {TABS.map(([k, l]) => (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              data-testid={`ad-version-${k}`}
              onClick={() => setTab(k)}
              className={`h-11 sm:h-9 rounded-md text-sm font-semibold transition-colors ${tab === k ? "bg-white text-amber-900 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}
            >
              {l}
            </button>
          ))}
        </div>
      )}

      {/* Application control bar (NOT part of the ad) */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-[11px] uppercase tracking-widest font-semibold text-stone-400 truncate">
          {versions ? (tab === "facebook_group" ? "Facebook Group ad" : "FB Page & Website ad") : "Assembled advertisement"}
        </span>
        <Button
          data-testid="copy-ad-text"
          onClick={copy}
          disabled={!canCopy}
          variant="outline"
          size="sm"
          className="rounded-full border-stone-300 h-10 sm:h-9 shrink-0"
        >
          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Text
        </Button>
      </div>
      </div>

      {versions && tab === "page_website" && current.warning && (
        <div data-testid="page-version-warning" className="mb-3 flex items-start gap-2 text-[12px] text-red-800 bg-red-50 border border-red-200 rounded px-2.5 py-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-px" /> {current.warning}
        </div>
      )}

      {/* Missing-data flags (NOT part of the ad) */}
      {warnings.length > 0 && (
        <div data-testid="ad-warnings" className="mb-3 space-y-1">
          {warnings.map((w, i) => (
            <div
              key={i}
              className="text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5"
            >
              ⚠ {w}
            </div>
          ))}
        </div>
      )}

      {/* The advertisement body — plain text, template spacing preserved */}
      <div
        data-testid="ad-body"
        className={`bg-white border border-stone-200 rounded-lg shadow-sm p-6 sm:p-7 ${canCopy ? "" : "opacity-60"}`}
      >
        {sections.map((s) => (
          <p
            key={s.id}
            data-testid={`ad-section-${s.id}`}
            className={`text-[15px] leading-relaxed text-stone-900 whitespace-pre-wrap break-words mb-4 last:mb-0 ${
              s.bold ? "font-bold" : ""
            } ${s.id === "headline" ? "font-medium" : ""}`}
          >
            {s.bold_prefix && s.text.startsWith(s.bold_prefix) ? <><strong>{s.bold_prefix}</strong>{s.text.slice(s.bold_prefix.length)}</> : s.text}
          </p>
        ))}
        {sections.length === 0 && (
          <p className="text-sm text-stone-500 italic">
            Not enough data to assemble this advertisement yet.
          </p>
        )}
      </div>
    </div>
  );
}
